/**
 * Grid entry — a whole round on one screen.
 *
 * The hole-at-a-time card is right when you are playing: big targets, one
 * group, no hunting. It is the wrong tool at the clubhouse with four paper
 * cards to catch up on, which is what this is for. Every player in the round,
 * every hole, type and move on.
 *
 * Anyone can edit anyone. That is not an oversight — it is how a scorer's card
 * already works in this app, and the person entering four cards after the fact
 * is by definition not the person who played them.
 */

import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { clockTime } from '../engine/clock'
import { holesFor } from '../engine/handicap'
import type { Tournament } from '../engine/types'
import { runMatch } from '../engine/formats'
import { buildContext, courseOf, currentRoundId, inPlayOrder, playerName, recalledRound, rememberRound, roundNet, roundPickerLabel, toParLabel, todayLocal } from '../select'
import { clearScore, isPending, mergedScores, setScore, useStore } from '../store'
import { Bar, Empty, UnverifiedBanner } from '../ui'
import { PhotoBackfill } from './PhotoBackfill'

export function Grid({ doc, header }: { doc: Tournament; header?: React.ReactNode }) {
  const scores = useStore(mergedScores)
  const [roundId, setRoundId] = useState(() => {
    const recalled = recalledRound()
    if (recalled && doc.rounds.some((r) => r.id === recalled)) return recalled
    return currentRoundId(
      doc,
      (id) => {
        const r = doc.rounds.find((x) => x.id === id)
        const c = r ? courseOf(doc, r) : undefined
        const gs = doc.groups.filter((g) => g.roundId === id)
        if (!r || !c || gs.length === 0) return false
        const holes = holesFor(c, r.nine)
        return gs.every((g) =>
          g.playerIds.every((pid) =>
            holes.every((h) =>
              scores.some(
                (x) => x.roundId === id && x.playerId === pid && x.holeNo === h.number,
              ),
            ),
          ),
        )
      },
      todayLocal(),
    )
  })
  useEffect(() => {
    if (roundId) rememberRound(roundId)
  }, [roundId])
  const round = doc.rounds.find((r) => r.id === roundId) ?? doc.rounds[0]
  const course = round ? courseOf(doc, round) : undefined

  const holes = useMemo(
    () => (course && round ? holesFor(course, round.nine) : []),
    [course, round],
  )

  const match = doc.matches.find((m) => m.roundId === round?.id)
  const ctx = useMemo(
    () => (round && match ? buildContext(doc, round, match, scores) : null),
    [doc, round, match, scores],
  )

  /*
   * On a quota round the figure beside a man's name is his POINTS, not his
   * strokes — Scott's ask. The grid is the whole-round view of a card, and
   * on these days a card is read in points; the strokes stay in the cells
   * where they were entered.
   */
  const quota = useMemo(() => {
    if (!ctx || !match || match.config.kind !== 'quota') return null
    const { tabs } = runMatch(ctx, match, {
      existing: doc.tiebreaks,
      seed: doc.settings.rngSeed,
    })
    const by = new Map<string, { total: string; sum: string; rank: number }>()
    const ranked = [...tabs].sort((a, b) => b.rankValue - a.rankValue)
    ranked.forEach((t, i) => {
      const pid = t.playerIds[0]
      if (pid) by.set(pid, { total: t.chip.primary, sum: t.chip.secondary, rank: i + 1 })
    })
    return by
  }, [ctx, match, doc.tiebreaks, doc.settings.rngSeed])

  /** Every cell in typing order, kept above the early return so hooks stay unconditional. */
  const cellRefs = useRef(new Map<string, HTMLInputElement | null>())
  /** Digits typed into the cell currently being filled, for two-digit scores. */
  const typed = useRef<{ key: string; text: string } | null>(null)
  /*
   * Whether the photo-backfill flow is open, and whether it could work if it
   * were. Both signals, not just the browser's flag — reading a photo needs a
   * server that actually answers, and a lodge wifi that never reaches the
   * internet reports online all afternoon.
   */
  const [photoOpen, setPhotoOpen] = useState(false)
  const canReadPhoto = useStore((s) => s.online && s.reachable)

  if (!round || !course || holes.length === 0) {
    return (
      <>
        <Bar title="Enter scores" />
        <Empty>No rounds set up yet. An admin can add them under Setup.</Empty>
      </>
    )
  }

  const groups = doc.groups
    .filter((g) => g.roundId === round.id)
    .sort((a, b) => a.index - b.index)

  const grossOf = (pid: string, holeNo: number): number | null => {
    const v = scores.find(
      (s) => s.roundId === round.id && s.playerId === pid && s.holeNo === holeNo,
    )?.value
    if (!v) return null
    if (v.state === 'holed') return v.gross
    if (v.state === 'pickedUp') return v.mostLikely
    return null
  }

  const cellKey = (pid: string, holeNo: number) => `${pid}:${holeNo}`

  const order = groups.flatMap((g) => g.playerIds)

  /**
   * Move on once the number cannot grow.
   *
   * A card is entered one player at a time, so the next cell is the next hole
   * across that player's row, and the end of a row falls through to the next
   * player. Nothing advances on a lone "1": it is the start of 10 through 15 as
   * often as it is an ace, and jumping early would put the second digit in the
   * next hole's box.
   */
  const advance = (pid: string, holeIdx: number) => {
    const row = order.indexOf(pid)
    const next =
      holeIdx + 1 < holes.length
        ? cellKey(pid, holes[holeIdx + 1]!.number)
        : row + 1 < order.length
          ? cellKey(order[row + 1]!, holes[0]!.number)
          : null
    if (!next) return
    const el = cellRefs.current.get(next)
    el?.focus()
    el?.select()
  }

  const enter = (pid: string, holeNo: number, holeIdx: number, raw: string) => {
    const n = Number(raw)
    /*
     * A blank now means "take it off the card", because there is finally a
     * way to say that — see `clearScore`. It used to mean nothing at all,
     * which left a number typed on the wrong row stuck there until an admin
     * wiped the entire card. Only a cell that HAS something is cleared, so
     * tabbing through empty boxes still writes nothing.
     */
    if (raw === '') {
      if (grossOf(pid, holeNo) != null) clearScore(round.id, pid, holeNo)
      return
    }
    if (!Number.isFinite(n) || n < 1 || n > 20) return
    setScore(round.id, pid, holeNo, { state: 'holed', gross: n })
    if (raw.length >= 2 || n >= 2) advance(pid, holeIdx)
  }

  /**
   * Typing is driven from the keystroke, not from React's change event.
   *
   * A controlled input fires no change when the value it is given matches the
   * value already rendered — so re-typing the 5 that is already in the box does
   * nothing at all, and the cursor sits there while the scorer wonders why. That
   * is not an edge case when you are copying a paper card: agreeing with what is
   * on screen is the single most common keystroke.
   *
   * Keying it directly also makes two-digit entry explicit. The digits are
   * buffered per cell, so 1 then 0 is ten rather than one-then-advance, and the
   * cell moves on the moment the number can no longer grow.
   */
  const onDigit = (pid: string, holeNo: number, holeIdx: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      typed.current = null
      return
    }
    if (!/^[0-9]$/.test(e.key)) return
    e.preventDefault()

    const key = cellKey(pid, holeNo)
    const prior = typed.current?.key === key ? typed.current.text : ''
    const text = (prior + e.key).slice(-2)
    typed.current = { key, text }

    const n = Number(text)
    if (n >= 1 && n <= 20) {
      setScore(round.id, pid, holeNo, { state: 'holed', gross: n })
    }
    // Two digits cannot grow further, and neither can a leading 2-9. Only a
    // lone 1 waits, since it still might become 10 through 15.
    if (text.length >= 2 || Number(e.key) >= 2) {
      typed.current = null
      advance(pid, holeIdx)
    }
  }

  const outPar = holes.reduce((s, h) => s + h.par, 0)

  return (
    <>
      {header}
      <Bar
        kicker={round.label}
        title="Enter scores"
        sub={`${course.name} · par ${outPar}`}
      />
      {!course.verified && <UnverifiedBanner courseName={course.name} />}

      <div className="px-4 py-3 border-b border-rule">
        <select
          value={round.id}
          onChange={(e) => setRoundId(e.target.value)}
          className="bc-label border border-ink px-2 py-2 bc-tap bg-paper w-full"
        >
          {/*
            The day and the match, in the order the week is played.
            
            This listed `r.label` in document order, and an eighteen-hole round
            added at Bandon is labeled "Bandon Dunes" — byte-identical to the
            scheduled one. A man entering four paper cards in the clubhouse
            picked one of two identical options and wrote a foursome's scores
            into the wrong round.
          */}
          {inPlayOrder(doc.rounds).map((r) => (
            <option key={r.id} value={r.id}>
              {roundPickerLabel(doc, r)}
            </option>
          ))}
        </select>
      </div>

      {/* Wider than a phone, so it scrolls inside its own box and the page body
          never moves sideways. */}
      <div className="overflow-x-auto">
        <table className="text-xs border-collapse min-w-max">
          <thead>
            <tr className="bg-ink text-paper">
              <th className="bc-label text-left px-3 py-2 sticky left-0 bg-ink z-10">Hole</th>
              {holes.map((h) => (
                <th key={h.number} className="bc-num px-1 py-2 w-11">
                  {h.number}
                </th>
              ))}
              <th className="bc-label px-3 py-2">Tot</th>
              <th className="bc-label px-3 py-2">Gross</th>
              <th className="bc-label px-3 py-2">Net</th>
            </tr>
            <tr className="bg-wash">
              <th className="bc-label text-left px-3 py-1 sticky left-0 bg-wash z-10">Par</th>
              {holes.map((h) => (
                <th key={h.number} className="bc-num px-1 py-1 text-mute font-semibold">
                  {h.par}
                </th>
              ))}
              <th className="bc-num px-3 py-1 text-mute">{outPar}</th>
              <th />
              <th />
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <Fragment key={g.id}>
                <tr className="bg-wash">
                  <td
                    className="bc-label text-mute px-3 py-1 sticky left-0 bg-wash z-10 whitespace-nowrap"
                    colSpan={1}
                  >
                    {clockTime(g.teeTime)}
                  </td>
                  <td colSpan={holes.length + 3} className="px-3 py-1 text-[12px] text-mute">
                    {g.playerIds.map((p) => playerName(doc, p)).join(' · ')}
                  </td>
                </tr>
                {g.playerIds.map((pid) => {
                  const total = holes.reduce((s, h) => s + (grossOf(pid, h.number) ?? 0), 0)
                  const net = ctx ? roundNet(ctx, pid) : null
                  /*
                   * Gross to par, and net to par, as two separate answers.
                   *
                   * The single column here was labelled Net and printed neither.
                   * `roundNet` nets a card by the pops allocated hole by hole,
                   * which is right for match play and quota — but the
                   * championship does not pop. It spends the whole handicap as a
                   * figure you START under par, so `ctx.pops` is all zeros and
                   * the "Net" column was quietly showing the gross: Rocky's 29
                   * printed +2 when his net was -1.
                   *
                   * So: gross to par from the holes actually played, and net
                   * from that less the full course handicap on a round that
                   * starts you under. Elsewhere the pops-based figure is still
                   * the honest one and is used unchanged.
                   */
                  const startsUnder = match?.config.kind === 'strokeNet'
                  const ch = ctx?.handicaps[pid]?.courseHandicap ?? 0
                  const playedPar = holes.reduce(
                    (s, h) => s + (grossOf(pid, h.number) == null ? 0 : h.par),
                    0,
                  )
                  const anyIn = holes.some((h) => grossOf(pid, h.number) != null)
                  const grossToPar = anyIn ? total - playedPar : null
                  const netToPar =
                    !anyIn
                      ? null
                      : startsUnder
                        ? total - playedPar - ch
                        : (net?.toPar ?? null)
                  return (
                    <tr key={pid} className="border-b border-rule">
                      <td className="bc-name px-3 py-1 sticky left-0 bg-paper z-10 whitespace-nowrap">
                        {playerName(doc, pid)}
                        {(() => {
                          const q = quota?.get(pid)
                          if (!q) return null
                          return (
                            <span className="ml-1.5 font-normal">
                              <span className="bc-num text-ink">{q.total}</span>
                              <span className="text-[11px] text-mute"> {q.sum}</span>
                            </span>
                          )
                        })()}
                      </td>
                      {holes.map((h, hi) => {
                        const pops = ctx?.pops[pid]?.[hi] ?? 0
                        const value = grossOf(pid, h.number)
                        const pending = isPending(round.id, pid, h.number)
                        const under = value != null && value - h.par < 0
                        return (
                          <td key={h.number} className="p-0.5 relative">
                            <input
                              ref={(el) => {
                                cellRefs.current.set(cellKey(pid, h.number), el)
                              }}
                              inputMode="numeric"
                              pattern="[0-9]*"
                              maxLength={2}
                              value={value ?? ''}
                              onChange={(e) => enter(pid, h.number, hi, e.target.value)}
                              onKeyDown={(e) => onDigit(pid, h.number, hi, e)}
                              onFocus={(e) => {
                                typed.current = null
                                e.currentTarget.select()
                              }}
                              aria-label={`${playerName(doc, pid)} hole ${h.number}`}
                              /*
                               * `bc-tap` because these are the smallest targets
                               * in the app and the most numerous: 216 of them on
                               * a full round, tapped with cold wet hands while
                               * walking. At 40 square they were under the 44px
                               * minimum every other control in the app is held
                               * to, and a near-miss here does not do nothing —
                               * it focuses the neighboring hole, where the next
                               * digit typed becomes somebody's score. The table
                               * already scrolls sideways inside its own box, so
                               * the extra 4px per column costs nothing but
                               * scroll.
                               */
                              /*
                                The same visual language as the signed card on
                                the Cards tab — Scott's ask: a filled box is
                                under par here too, and a birdie on a stroke
                                hole pulls its fill in off the edges
                                (`bc-popfill`) so the pop ring keeps sitting on
                                paper. The fill outranks the pending wash; a
                                score's worth beats its delivery status.
                              */
                              className={`bc-tap w-10 h-10 text-center bc-num border ${
                                value == null ? 'border-rule text-dim' : 'border-ink'
                              } ${
                                under
                                  ? `bg-signal text-paper${pops > 0 ? ' bc-popfill p-[7px]' : ''}`
                                  : pending
                                    ? 'bg-wash'
                                    : 'bg-paper'
                              }`}
                            />
                            {/*
                              A stroke received, made hard to miss.

                              This was a 4px dot in the corner, which failed
                              three ways: it is the smallest mark in the app, it
                              sat on the row's own border so you could not tell
                              whose stroke it was, and on a cell already filled
                              for an under-par score it disappeared. A ring
                              belongs unambiguously to one box and survives any
                              ground. Inset so it never covers a digit.

                              `pop`, not `signal`. A stroke received is something
                              a man was GIVEN and an under-par score is something
                              he DID, and while both were red the two marks were
                              indistinguishable — see the token in index.css.

                              3px, because 2px reads as a border rather than as a
                              mark. There is room: the cell is 44px, the ring is
                              inset 2px, so 3px of stroke still leaves a 34px
                              square of clear space around a one or two digit
                              number.
                            */}
                            {pops > 0 && (
                              <span
                                aria-hidden="true"
                                className="pointer-events-none absolute inset-0.5 border-[3px] border-pop"
                              />
                            )}
                            {pops > 1 && (
                              <span className="pointer-events-none absolute bottom-0.5 right-1 text-[9px] font-extrabold leading-none text-pop">
                                {pops}
                              </span>
                            )}
                          </td>
                        )
                      })}
                      <td className="bc-num px-3 text-center">{total || '·'}</td>
                      <td className="bc-num px-3 text-center text-mute">
                        {grossToPar == null ? '·' : toParLabel(grossToPar)}
                      </td>
                      {/* The figure the championship is actually scored on. */}
                      <td className="bc-num px-3 text-center text-ink font-bold">
                        {netToPar == null ? '·' : toParLabel(netToPar)}
                      </td>
                    </tr>
                  )
                })}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-4 pt-4">
        {/* Disabled rather than hidden when offline: a button that vanishes
            teaches people the feature is flaky, and the label says exactly
            what it is waiting for. */}
        <button
          type="button"
          disabled={!canReadPhoto}
          onClick={() => setPhotoOpen(true)}
          className="bc-tap bc-label border-2 border-ink px-4 py-3 w-full disabled:opacity-40"
        >
          {canReadPhoto ? 'From a photo of the card' : 'From a photo of the card — needs signal'}
        </button>
      </div>

      <p className="px-4 py-4 text-[13px] text-mute leading-snug">
        Type the gross — it saves as you go and goes up when there is signal. A
        shaded box has not reached the server yet; a filled box is under par; a
        ringed box is a hole you get a stroke on. <b>Emptying a box takes that score
        off the card</b> — for the number typed on the wrong row. Once a card is signed
        an admin has to unlock it first.
      </p>

      {photoOpen && (
        <PhotoBackfill doc={doc} roundId={round.id} onClose={() => setPhotoOpen(false)} />
      )}
    </>
  )
}
